import { Controller, useFormContext } from 'react-hook-form';

import FormHelperText from '@mui/material/FormHelperText';

import { Upload, UploadBox, UploadAvatar } from '../upload';

import { useAddEmployeeFile } from 'src/actions/teams/teamActions.ts';
import { photoInsert } from 'src/actions/loyalty/loyaltyActions';
import { toast } from 'src/components/snackbar';
import { useState } from 'react';

import { CircularProgress, Stack } from '@mui/material';

export function RHFUploadAvatar({ name, onFileUpload, uploaderSize, ...other }) {
  const { control, setValue } = useFormContext();
  const { uploadedFile } = useAddEmployeeFile();
  const [loadingAvatar, setLoadingAvatar] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const onDrop = async (acceptedFiles) => {
          setLoadingAvatar(true);
          const file = acceptedFiles[0];
          const formData = new FormData();
          formData.append('File', file);
          formData.append('IsOuter', 'true');
          formData.append('Name', file.name);

          try {
            const response = await uploadedFile(formData);
            const { fileId, fullPath } = response.data;

            onFileUpload({ fileId, fullPath });
            setValue(name, file, { shouldValidate: true });
          } catch (err) {
            console.error('Error uploading file:', err);
          } finally {
            setLoadingAvatar(false);
          }
        };

        return (
          <Stack alignItems="center" spacing={1}>
            <div
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <UploadAvatar
                uploaderSize={uploaderSize}
                value={field.value}
                error={!!error}
                onDrop={loadingAvatar ? () => {} : onDrop} // Disable drop when loading
                {...other}
              />
              {loadingAvatar && (
                <CircularProgress
                  size={48}
                  sx={{
                    position: 'absolute',
                    top: '35%',
                    left: '45%',
                    // transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
            </div>

            {!!error && (
              <FormHelperText error sx={{ px: 2, textAlign: 'center' }}>
                {error.message}
              </FormHelperText>
            )}
          </Stack>
        );
      }}
    />
  );
}
export function RHFUploadPhoto({ name, onFileUpload, uploaderSize, ...other }) {
  const { control, setValue } = useFormContext();
  const [loadingAvatar, setLoadingAvatar] = useState(false);
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const onDrop = async (acceptedFiles) => {
          setLoadingAvatar(true);
          const file = acceptedFiles[0];

          try {
            const response = await photoInsert(file);

            if (response) {
              const { fileId, path } = response;

              onFileUpload({ fileId, path });

              setValue(name, { fileId, path }, { shouldValidate: true });
            }
          } catch (err) {
            console.error('Error uploading photo:', err);
          } finally {
            setLoadingAvatar(false);
          }
        };

        return (
          <Stack alignItems="center" spacing={1}>
            <div
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <UploadAvatar
                value={field.value}
                uploaderSize={uploaderSize}
                error={!!error}
                onDrop={onDrop}
                {...other}
              />
              {loadingAvatar && (
                <CircularProgress
                  size={48}
                  sx={{
                    position: 'absolute',
                    top: '35%',
                    left: '45%',
                    // transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
              {!!error && (
                <FormHelperText error sx={{ px: 2, textAlign: 'center' }}>
                  {error.message}
                </FormHelperText>
              )}
            </div>
          </Stack>
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFUploadBox({ name, ...other }) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <UploadBox value={field.value} error={!!error} {...other} />
      )}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFUpload({ name, multiple, helperText, isUploading, ...other }) {
  const { control, setValue } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const uploadProps = {
          multiple,
          accept: { 'image/*': [] },
          error: !!error,
          helperText: error?.message ?? helperText,
        };

        const onDrop = (acceptedFiles) => {
          const value = multiple ? [...field.value, ...acceptedFiles] : acceptedFiles[0];

          setValue(name, value, { shouldValidate: true });
        };

        return (
          <Upload
            {...uploadProps}
            value={field.value}
            onDrop={onDrop}
            {...other}
            isUploading={isUploading}
          />
        );
      }}
    />
  );
}
